import { useState, useRef, type DragEvent } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import toast from 'react-hot-toast';
import type { Media } from '@seencast/shared';
import { useMedia } from '../../api/media.ts';
import { useBulkAddToPlaylists } from '../../api/playlists.ts';
import { CONTENT_KINDS, type ContentKind } from '../../api/templates.ts';
import { usePermissions } from '../../auth/session.tsx';
import { Button, Loading, ErrorState, EmptyState, Meter, Badge } from '../../components/ui.tsx';
import { MediaThumb } from '../../components/MediaThumb.tsx';
import { MediaUploadIcon } from '../../onboarding/icons.tsx';
import { formatBytes, formatDuration, titleCase } from '../../lib/format.ts';
import { useUploads } from './useUploads.ts';
import { AddUrlModal } from './AddUrlModal.tsx';
import { MediaDetailDrawer } from './MediaDetailDrawer.tsx';
import { TemplateGalleryModal } from './TemplateGalleryModal.tsx';
import { TemplateForm } from './TemplateForm.tsx';
import {
  PlaylistMultiPicker,
  addedToPlaylistsMessage,
  bulkAddErrorMessage,
} from './PlaylistMultiPicker.tsx';

export interface MediaSearch {
  media?: string;
  kind?: ContentKind;
  q?: string;
  addUrl?: boolean;
  create?: boolean;
  template?: string;
}

const KIND_TAB_LABEL: Record<ContentKind, string> = {
  image: 'Images',
  video: 'Video',
  url: 'Web pages',
  html_template: 'Templates',
  html_snippet: 'Snippets',
};

/** Sub-label under a grid tile — duration for video, a friendly kind otherwise. */
function tileSubtitle(m: { kind: string; size_bytes: number | null; duration_ms: number | null }): string {
  switch (m.kind) {
    case 'video':
      return formatDuration(m.duration_ms);
    case 'url':
      return 'web page';
    case 'html_template':
      return 'template slide';
    case 'html_snippet':
      return 'code snippet';
    default:
      return formatBytes(m.size_bytes);
  }
}

/** The selection checkbox glyph (inline SVG check when on; empty box via CSS). */
function CheckGlyph({ on }: { on: boolean }) {
  if (!on) return null;
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 12.5l4.5 4.5L19 7" />
    </svg>
  );
}

/** One media grid tile. Owns the hover/long-press select affordance so the grid
 *  can stay a flat map. Tap opens the drawer; in selection mode tap toggles. */
function MediaTile({
  media,
  selectable,
  selected,
  selectMode,
  onOpen,
  onToggle,
}: {
  media: Media;
  selectable: boolean;
  selected: boolean;
  selectMode: boolean;
  onOpen: (id: string) => void;
  onToggle: (id: string) => void;
}) {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressed = useRef(false);

  const start = () => {
    if (!selectable) return;
    longPressed.current = false;
    timer.current = setTimeout(() => {
      longPressed.current = true;
      onToggle(media.id); // long-press enters/extends selection (touch pattern)
    }, 450);
  };
  const cancel = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  };
  const handleClick = () => {
    if (longPressed.current) {
      longPressed.current = false; // the long-press already toggled; swallow the click
      return;
    }
    if (selectable && selectMode) onToggle(media.id);
    else onOpen(media.id);
  };

  return (
    <div
      className={`media-tile${selected ? ' selected' : ''}`}
      onClick={handleClick}
      onPointerDown={start}
      onPointerUp={cancel}
      onPointerLeave={cancel}
    >
      {selectable && (
        <button
          type="button"
          className="media-tile-check"
          role="checkbox"
          aria-checked={selected}
          aria-label={`${selected ? 'Deselect' : 'Select'} ${media.filename ?? 'item'}`}
          onClick={(e) => {
            e.stopPropagation();
            onToggle(media.id);
          }}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <CheckGlyph on={selected} />
        </button>
      )}
      <MediaThumb media={media} />
      <div className="media-meta">
        <div className="name">{media.filename}</div>
        <div className="sub">{tileSubtitle(media)}</div>
      </div>
    </div>
  );
}

export function MediaPage() {
  const search = useSearch({ strict: false }) as MediaSearch;
  const navigate = useNavigate();
  const perms = usePermissions();
  const uploads = useUploads();
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Bulk selection (S4). selectMode = at least one item picked; then checkboxes
  // stay visible and the selection bar shows. Tap-to-open reverts once cleared.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [pickerOpen, setPickerOpen] = useState(false);
  const bulkAdd = useBulkAddToPlaylists();
  const selectMode = selected.size > 0;

  const toggleSelect = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const clearSelection = () => setSelected(new Set());

  const setSearch = (patch: Partial<MediaSearch>) =>
    navigate({ to: '/media', search: (prev) => ({ ...prev, ...patch }) });

  const media = useMedia({ kind: search.kind, q: search.q });

  function handleAddToPlaylists(playlistIds: string[]) {
    const mediaIds = [...selected];
    bulkAdd.mutate(
      { media_ids: mediaIds, playlist_ids: playlistIds },
      {
        onSuccess: () => {
          toast.success(addedToPlaylistsMessage(mediaIds.length, playlistIds.length));
          setPickerOpen(false);
          clearSelection();
        },
        onError: (e) => {
          toast.error(bulkAddErrorMessage(e));
          // All-or-nothing: a 422 means a selected item vanished (concurrent
          // delete). Refresh the grid so the stale tile disappears.
          void media.refetch();
        },
      },
    );
  }

  function onDrop(e: DragEvent) {
    e.preventDefault();
    setDragging(false);
    if (!perms.canManageContent) return;
    const files = Array.from(e.dataTransfer.files);
    if (files.length) uploads.start(files);
  }

  // Show every upload (in-progress, done, or failed) until "Clear finished".
  const activeUploads = uploads.items;

  return (
    <div
      className="page"
      onDragOver={(e) => {
        if (perms.canManageContent) {
          e.preventDefault();
          setDragging(true);
        }
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={onDrop}
    >
      <div className="page-header">
        <div>
          <h1>Media</h1>
          <div className="subtitle">Images, video, and web pages to show on screens.</div>
        </div>
        {perms.canManageContent && (
          <div className="row">
            <Button onClick={() => setSearch({ addUrl: true })}>Add URL</Button>
            <Button onClick={() => inputRef.current?.click()}>Upload</Button>
            <Button variant="primary" onClick={() => setSearch({ create: true })}>
              Create
            </Button>
            <input
              ref={inputRef}
              type="file"
              accept="image/*,video/*"
              multiple
              hidden
              onChange={(e) => {
                if (e.target.files) uploads.start(Array.from(e.target.files));
                e.target.value = '';
              }}
            />
          </div>
        )}
      </div>

      {perms.canManageContent && (
        <div className={`dropzone${dragging ? ' drag' : ''}`}>
          {dragging ? 'Drop to upload' : 'Drag files here to upload, or use the Upload button.'}
        </div>
      )}

      {activeUploads.length > 0 && (
        <div className="card card-pad mb-4">
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <strong>Uploads</strong>
            <Button size="sm" variant="ghost" onClick={uploads.clearFinished}>
              Clear finished
            </Button>
          </div>
          {activeUploads.map((u) => (
            <div className="upload-row" key={u.id}>
              <span className="name">{u.filename}</span>
              {u.phase === 'error' ? (
                <Badge tone="warn">{u.error ?? 'Failed'}</Badge>
              ) : u.phase === 'done' ? (
                <Badge tone="ok">Done</Badge>
              ) : (
                <>
                  <Meter value={u.progress * 100} max={100} />
                  <span className="text-dim" style={{ fontSize: 'var(--fs-xs)', width: 70 }}>
                    {titleCase(u.phase)}
                  </span>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="toolbar">
        <input
          className="search"
          aria-label="Search media"
          placeholder="Search by name…"
          value={search.q ?? ''}
          onChange={(e) => setSearch({ q: e.target.value || undefined })}
        />
        <div className="seg" role="tablist" aria-label="Filter by kind">
          <button className={!search.kind ? 'active' : ''} onClick={() => setSearch({ kind: undefined })}>
            All
          </button>
          {CONTENT_KINDS.map((k) => (
            <button key={k} className={search.kind === k ? 'active' : ''} onClick={() => setSearch({ kind: k })}>
              {KIND_TAB_LABEL[k]}
            </button>
          ))}
        </div>
      </div>

      {media.isLoading ? (
        <Loading />
      ) : media.isError ? (
        <ErrorState message="Could not load media." onRetry={() => media.refetch()} />
      ) : media.data!.length === 0 && !search.q && !search.kind ? (
        <div className="card card-pad">
          <EmptyState icon={<MediaUploadIcon />} title="No media yet">
            <p>Drag images or video anywhere on this page — or create a slide from a template, no file required.</p>
            {perms.canManageContent && (
              <div className="mt-4">
                <Button variant="primary" onClick={() => inputRef.current?.click()}>
                  Upload
                </Button>
              </div>
            )}
          </EmptyState>
        </div>
      ) : media.data!.length === 0 ? (
        <div className="card card-pad text-dim">No media matches these filters.</div>
      ) : (
        <>
          {selectMode && perms.canManageContent && (
            <div className="selection-bar" role="region" aria-label="Bulk actions">
              <span className="selection-count">
                {selected.size} selected
              </span>
              <div className="selection-actions">
                <Button size="sm" variant="primary" onClick={() => setPickerOpen(true)}>
                  Add to playlists…
                </Button>
                <Button size="sm" variant="ghost" onClick={clearSelection}>
                  Clear
                </Button>
              </div>
            </div>
          )}
          <div className={`media-grid${selectMode ? ' selecting' : ''}`}>
            {media.data!.map((m) => (
              <MediaTile
                key={m.id}
                media={m}
                selectable={perms.canManageContent}
                selected={selected.has(m.id)}
                selectMode={selectMode}
                onOpen={(id) => setSearch({ media: id })}
                onToggle={toggleSelect}
              />
            ))}
          </div>
        </>
      )}

      {search.addUrl && <AddUrlModal onClose={() => setSearch({ addUrl: undefined })} />}
      {search.create && (
        <TemplateGalleryModal
          onPick={(templateId) => setSearch({ create: undefined, template: templateId })}
          onClose={() => setSearch({ create: undefined })}
        />
      )}
      {search.template && (
        <TemplateForm
          templateId={search.template}
          onClose={() => setSearch({ template: undefined })}
          // Save lands the new slide in the grid (the mutation invalidates ['media']);
          // the user opens it to edit fields, same as any other media.
          onSaved={() => setSearch({ template: undefined })}
        />
      )}
      {search.media && (
        <MediaDetailDrawer mediaId={search.media} onClose={() => setSearch({ media: undefined })} />
      )}
      {pickerOpen && (
        <PlaylistMultiPicker
          mediaCount={selected.size}
          busy={bulkAdd.isPending}
          onConfirm={handleAddToPlaylists}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </div>
  );
}
